/**
 * Geteilte Berechnung der Be-/Fahrt-/Abladephasen eines Transports (NT-TRANSPORT-BELADUNG-01).
 * Genutzt von lagerhaus.ts (Lagerhaus-Versand) und crimeOps.ts (Dealer-Belieferung) — beide
 * Konsumenten bauen ihr eigenes Markup/CSS mit ihrer jeweiligen Klassenkonvention, nutzen aber
 * dieselbe Phasen-Grenzen-Mathematik, um Divergenzen zwischen den beiden Kopien zu vermeiden.
 *
 * Farben (Vorgabe Chris, NT-TRANSPORT-BELADUNG-01 Soll-Verhalten): Verladung Orange/Kupfer
 * (--accent-orange, NICHT --accent-red, das steht für illegal/Gefahr), Fahrt Gelb
 * (--accent-gold — bereits an anderer Stelle in lagerhaus.ts für "LIEFERUNG" genutzt), Abladung
 * Grün (--accent-green).
 */

export type TransportPhaseKey = 'VERLADUNG' | 'LIEFERUNG' | 'ABLADUNG';

export interface TransportPhaseSegment {
  key: TransportPhaseKey;
  label: string;
  colorVar: string; // CSS Custom Property, z.B. "--accent-orange"
  widthPct: number; // Anteil an der Balkenbreite (proportional zur Phasendauer)
  fillPct: number;  // 0-100, Fortschritt innerhalb dieses Segments
  isActive: boolean;
}

export interface TransportPhaseUi {
  segments: TransportPhaseSegment[];
  activePhase: TransportPhaseKey;
  activeLabel: string;
}

export interface TransportPhaseTimestamps {
  startedAt: string;
  loadingEndsAt: string | null;
  unloadingStartsAt: string | null;
  arrivesAt: string;
}

const PHASE_LABELS: Record<TransportPhaseKey, string> = {
  VERLADUNG: 'VERLADUNG',
  LIEFERUNG: 'LIEFERUNG',
  ABLADUNG: 'ABLADUNG',
};

const PHASE_COLOR_VARS: Record<TransportPhaseKey, string> = {
  VERLADUNG: '--accent-orange',
  LIEFERUNG: '--accent-gold',
  ABLADUNG: '--accent-green',
};

/**
 * Liefert null, wenn keine Phasen-Zeitpunkte vorliegen (Alt-Transport vor NT-TRANSPORT-BELADUNG-01,
 * oder Markt-Kauf, der grundsätzlich keine Verlade-/Abladephase hat, B2) — der Aufrufer zeigt dann
 * die bisherige, durchgehende "LIEFERUNG"-Anzeige (gelb) statt des Drei-Segmente-Balkens.
 */
export function computeTransportPhaseUi(t: TransportPhaseTimestamps, nowMs: number): TransportPhaseUi | null {
  if (t.loadingEndsAt === null || t.unloadingStartsAt === null) return null;

  const startedAtMs         = new Date(t.startedAt).getTime();
  const loadingEndsAtMs     = new Date(t.loadingEndsAt).getTime();
  const unloadingStartsAtMs = new Date(t.unloadingStartsAt).getTime();
  const arrivesAtMs         = new Date(t.arrivesAt).getTime();

  const totalMs = arrivesAtMs - startedAtMs;
  if (!(totalMs > 0)) return null;

  const bounds: [TransportPhaseKey, number, number][] = [
    ['VERLADUNG', startedAtMs, loadingEndsAtMs],
    ['LIEFERUNG', loadingEndsAtMs, unloadingStartsAtMs],
    ['ABLADUNG', unloadingStartsAtMs, arrivesAtMs],
  ];

  let activePhase: TransportPhaseKey = 'ABLADUNG';
  const segments = bounds.map(([key, segStart, segEnd]) => {
    const segMs = Math.max(0, segEnd - segStart);
    const fillPct = segMs === 0 ? 100 : Math.min(100, Math.max(0, ((nowMs - segStart) / segMs) * 100));
    const isActive = nowMs >= segStart && nowMs < segEnd;
    if (isActive) activePhase = key;
    return {
      key,
      label: PHASE_LABELS[key],
      colorVar: PHASE_COLOR_VARS[key],
      widthPct: (segMs / totalMs) * 100,
      fillPct,
      isActive,
    };
  });

  return { segments, activePhase, activeLabel: PHASE_LABELS[activePhase] };
}
